import axios from "axios";
import { resolveShopApiUrl } from "./shopApi";
import { getFirebaseIDTokenForShop } from "../firebase/firebase";

/**
 * Thin client for the SooCool endpoints on a shop's own `api` project.
 *
 * Unlike `trunkrs.js`, nothing carrier-specific lives in the browser here: the SooCool API key,
 * the colli calculation and the Firestore write-back all run server-side in `api/`. The only
 * credential this module holds is the user's Firebase ID token, and it is always the token of
 * the shop that owns the order - each api deploy (VCE / HVO) verifies against its own Firebase
 * project and only knows its own Firestore.
 */

const SOOCOOL_BASE_PATH = "/api/shipping/soocool";

/**
 * Pull a human readable message out of an axios error.
 *
 * The api answers errors as JSON, but the label call runs with `responseType: "blob"`, so an
 * error body arrives as a Blob there and has to be read back to text before it says anything.
 */
const readErrorMessage = async (err, fallback) => {
  const data = err?.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        return parsed.message || parsed.error || text || fallback;
      } catch (parseError) {
        return text || fallback;
      }
    } catch (blobError) {
      return fallback;
    }
  }

  if (typeof data === "string" && data.length > 0) return data;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  if (err?.message) return err.message;
  return fallback;
};

/**
 * Hand a batch of orders to SooCool, in one call for the whole batch.
 *
 * @param {string} shopSource "VCE" or "HVO" - picks both the api host and the ID token
 * @param {string[]} orderIds our Firestore document ids (not the human order numbers)
 * @returns {Promise<Array<{orderId: string, status: "created"|"skipped"|"failed", soocool: Object|null, error: string|null}>>}
 *          one result per requested order; a single failing order does not fail the rest
 */
export const createSooCoolShipments = async (shopSource, orderIds) => {
  const idToken = await getFirebaseIDTokenForShop(shopSource);

  try {
    const response = await axios.post(
      `${resolveShopApiUrl(shopSource)}${SOOCOOL_BASE_PATH}/orders`,
      { orderIds },
      { headers: { Authorization: `${idToken}` } },
    );
    return response.data?.results || [];
  } catch (err) {
    throw new Error(await readErrorMessage(err, "SooCool export mislukt"));
  }
};

/**
 * Look up what SooCool itself holds, without touching anything.
 *
 * This proxies SooCool's `GET /order` through our api (the API key is server-side only) and is
 * strictly read-only: it exists so staff can check a day's shipments without logging into
 * SooCool's own portal. Because `api/` is deployed once per shop, the shop has to be chosen
 * explicitly - a VCE token against the HVO deploy simply does not resolve.
 *
 * @param {string} shopSource "VCE" or "HVO" - picks both the api host and the ID token
 * @param {{deliveryDate?: string, pickupDate?: string, orderReference?: string, barcode?: string}} filters
 *        dates as YYYY-MM-DD; at least one filter is required or the api answers 400
 * @returns {Promise<Array<Object>>} SooCool Order objects, verbatim as SooCool returned them
 */
export const searchSooCoolOrders = async (shopSource, filters = {}) => {
  // Empty strings would be sent as `?barcode=` and count as a filter on SooCool's side, which
  // turns a date search into a search for the empty barcode. Drop them before they leave.
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== null && `${value}`.trim() !== ""),
  );

  if (Object.keys(params).length === 0) {
    throw new Error("Geef minimaal een datum, bestelnummer of barcode op om in SooCool te zoeken");
  }

  const idToken = await getFirebaseIDTokenForShop(shopSource);

  try {
    const response = await axios.get(`${resolveShopApiUrl(shopSource)}${SOOCOOL_BASE_PATH}/search`, {
      params,
      headers: { Authorization: `${idToken}` },
    });
    return response.data?.orders || [];
  } catch (err) {
    throw new Error(await readErrorMessage(err, "Zoeken in SooCool mislukt"));
  }
};

/**
 * Cancel transport orders at SooCool, from the SooCool page.
 *
 * Takes SooCool's own order ids - that page lists what SooCool holds - and the api releases our
 * order for each: its `soocool` block comes off, so it can be exported again. SooCool refuses a
 * delivery that is already planned (412); that order comes back failed and the others still go.
 *
 * @param {string} shopSource "VCE" or "HVO" - the shop whose SooCool account the orders are in
 * @param {number[]} orderIds SooCool order ids
 * @returns {Promise<Array<{sooCoolOrderId: number, orderReference: string|null, status: "cancelled"|"failed",
 *          releasedDocumentId: string|null, note: string|null, error: string|null}>>}
 */
export const cancelSooCoolOrders = async (shopSource, orderIds) => {
  const idToken = await getFirebaseIDTokenForShop(shopSource);

  try {
    const response = await axios.post(
      `${resolveShopApiUrl(shopSource)}${SOOCOOL_BASE_PATH}/cancel`,
      { orderIds },
      { headers: { Authorization: `${idToken}` } },
    );
    return response.data?.results || [];
  } catch (err) {
    throw new Error(await readErrorMessage(err, "Annuleren bij SooCool mislukt"));
  }
};

/**
 * Fetch the shipping labels for a set of orders as one PDF.
 *
 * The api resolves our document ids to SooCool goodIds, so the returned PDF holds a label for
 * every colli of every requested order - that is what makes multi-colli printable at all.
 *
 * @param {string} shopSource "VCE" or "HVO"
 * @param {string[]} orderIds our Firestore document ids
 * @param {string} output "a6" (one label per page, the sticker printer) or "collated_a4"
 * @returns {Promise<Blob>} a PDF blob
 */
export const fetchSooCoolLabelPdf = async (shopSource, orderIds, output = "a6") => {
  const idToken = await getFirebaseIDTokenForShop(shopSource);

  try {
    const response = await axios.get(`${resolveShopApiUrl(shopSource)}${SOOCOOL_BASE_PATH}/label`, {
      params: { orderIds: orderIds.join(","), output },
      headers: { Authorization: `${idToken}` },
      responseType: "blob",
    });
    // Force the mime type: without it a blob that arrives without a Content-Type opens as a
    // download of unknown kind instead of a PDF.
    return new Blob([response.data], { type: "application/pdf" });
  } catch (err) {
    throw new Error(await readErrorMessage(err, "Ophalen van SooCool labels mislukt"));
  }
};
